import React, { useState } from 'react';
import {
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
    Typography,
    Card,
    CardContent,
    CardActionArea,
    Grid,
    Chip,
    Slider,
    FormControlLabel,
    Switch,
    Stack,
    MobileStepper,
    useTheme,
    useMediaQuery
} from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import HomeIcon from '@mui/icons-material/Home';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import { Exercise } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

interface AIWorkoutWizardProps {
    exercises: Exercise[];
    onGenerate: (prompt: string, useExisting: boolean) => void;
    onCancel: () => void;
    isLoading: boolean;
}

type TrainingLocation = 'gym' | 'home';
type TrainingType = 'hypertrophy' | 'strength' | 'resistance';
type DurationMode = 'minutes' | 'exercises';
type WorkoutLevel = 'beginner' | 'experienced';

interface WizardState {
    location: TrainingLocation | null;
    muscles: string[];
    type: TrainingType | null;
    durationMode: DurationMode;
    durationValue: number;
    level: WorkoutLevel | null;
    useExisting: boolean;
}

const MUSCLE_GROUPS = [
    'Chest',
    'Back',
    'Shoulders',
    'Abs',
    'Quadriceps',
    'Hamstrings',
    'Glutes',
    'Calves',
    'Adductors',
    'Biceps',
    'Triceps',
    'Cardio/Running',
    'Swimming'
];

export const AIWorkoutWizard: React.FC<AIWorkoutWizardProps> = ({
    exercises,
    onGenerate,
    onCancel,
    isLoading
}) => {
    const { t } = useLanguage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeStep, setActiveStep] = useState(0);
    const [state, setState] = useState<WizardState>({
        location: null,
        muscles: [],
        type: null,
        durationMode: 'minutes',
        durationValue: 45,
        level: null,
        useExisting: false
    });

    // Keys for translation lookups
    const steps = [
        'aiWizard.steps.location',
        'aiWizard.steps.muscles',
        'aiWizard.steps.type',
        'aiWizard.steps.duration',
        'aiWizard.steps.level',
        'aiWizard.steps.exercises',
        'aiWizard.steps.summary'
    ];

    const handleNext = () => {
        if (activeStep === steps.length - 1) {
            generatePrompt();
        } else {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const updateState = (key: keyof WizardState, value: any) => {
        setState((prev) => ({ ...prev, [key]: value }));
    };

    const toggleMuscle = (muscle: string) => {
        setState((prev) => {
            const exists = prev.muscles.includes(muscle);
            if (exists) {
                return { ...prev, muscles: prev.muscles.filter(m => m !== muscle) };
            }
            return { ...prev, muscles: [...prev.muscles, muscle] };
        });
    };

    const generatePrompt = () => {
        const musclesStr = state.muscles.length > 0 ? state.muscles.join(', ') : 'Whole Body';
        const durationStr = state.durationMode === 'minutes'
            ? `${state.durationValue} minutes`
            : `${state.durationValue} exercises`;

        let prompt = `Create a ${state.type} workout for a ${state.level} user at a ${state.location}. `;
        prompt += `Target muscles: ${musclesStr}. Constraint: The workout must STRICTLY focus on ${musclesStr}. `;
        prompt += `Duration/Volume: ${durationStr}. `;

        if (state.useExisting) {
            prompt += `Constraint: Use ONLY my existing exercises. `;
        }

        if (state.location === 'home') {
            prompt += `Constraint: Do not use any equipment other than dumbbells (bodyweight exercises are allowed). `;
        }

        onGenerate(prompt, state.useExisting);
    };

    const renderLocationStep = () => (
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
                <Card
                    elevation={state.location === 'home' ? 8 : 1}
                    sx={{ border: state.location === 'home' ? '2px solid primary.main' : 'none' }}
                >
                    <CardActionArea onClick={() => { updateState('location', 'home'); handleNext(); }} sx={{ p: 4, textAlign: 'center' }}>
                        <HomeIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6">{t('aiWizard.location.home')}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('aiWizard.location.homeDesc')}</Typography>
                    </CardActionArea>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <Card
                    elevation={state.location === 'gym' ? 8 : 1}
                    sx={{ border: state.location === 'gym' ? '2px solid primary.main' : 'none' }}
                >
                    <CardActionArea onClick={() => { updateState('location', 'gym'); handleNext(); }} sx={{ p: 4, textAlign: 'center' }}>
                        <FitnessCenterIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6">{t('aiWizard.location.gym')}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('aiWizard.location.gymDesc')}</Typography>
                    </CardActionArea>
                </Card>
            </Grid>
        </Grid>
    );

    const getMuscleLabel = (muscle: string) => {
        const keyMap: Record<string, string> = {
            'Chest': 'chest',
            'Back': 'back',
            'Shoulders': 'shoulders',
            'Abs': 'abs',
            'Quadriceps': 'quadriceps',
            'Hamstrings': 'hamstrings',
            'Glutes': 'glutes',
            'Calves': 'calves',
            'Adductors': 'adductors',
            'Biceps': 'biceps',
            'Triceps': 'triceps',
            'Cardio/Running': 'cardio',
            'Swimming': 'swimming'
        };
        return t(`aiWizard.muscles.${keyMap[muscle] || 'chest'}`);
    };

    const renderMusclesStep = () => (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography gutterBottom variant="h6">{t('aiWizard.muscles.title')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 2 }}>
                {MUSCLE_GROUPS.map((muscle) => (
                    <Chip
                        key={muscle}
                        label={getMuscleLabel(muscle)}
                        onClick={() => toggleMuscle(muscle)}
                        color={state.muscles.includes(muscle) ? 'primary' : 'default'}
                        variant={state.muscles.includes(muscle) ? 'filled' : 'outlined'}
                        sx={{ p: 1 }}
                        clickable
                    />
                ))}
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
                {t('aiWizard.muscles.subtitle')}
            </Typography>
        </Box>
    );

    const renderTypeStep = () => (
        <Grid container spacing={2} sx={{ mt: 2 }} justifyContent="center">
            {[
                { value: 'hypertrophy', label: t('aiWizard.type.hypertrophy'), desc: t('aiWizard.type.hypertrophyDesc') },
                { value: 'strength', label: t('aiWizard.type.strength'), desc: t('aiWizard.type.strengthDesc') },
                { value: 'resistance', label: t('aiWizard.type.resistance'), desc: t('aiWizard.type.resistanceDesc') }
            ].map((type) => (
                <Grid size={{ xs: 12, sm: 6 }} key={type.value}>
                    <Card variant="outlined" sx={{
                        borderColor: state.type === type.value ? 'primary.main' : undefined,
                        bgcolor: state.type === type.value ? 'action.selected' : undefined,
                        height: '100%'
                    }}>
                        <CardActionArea
                            onClick={() => { updateState('type', type.value); handleNext(); }}
                            sx={{ height: '100%' }}
                        >
                            <CardContent>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>{type.label}</Typography>
                                <Typography variant="body2" color="text.secondary">{type.desc}</Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    const renderDurationStep = () => (
        <Box sx={{ mt: 4, width: '100%' }}>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
                <Button
                    variant={state.durationMode === 'minutes' ? 'contained' : 'outlined'}
                    onClick={() => {
                        updateState('durationMode', 'minutes');
                        updateState('durationValue', 45);
                    }}
                    startIcon={<AccessTimeIcon />}
                >
                    {t('aiWizard.duration.minutes')}
                </Button>
                <Button
                    variant={state.durationMode === 'exercises' ? 'contained' : 'outlined'}
                    onClick={() => {
                        updateState('durationMode', 'exercises');
                        updateState('durationValue', 6);
                    }}
                    startIcon={<FormatListNumberedIcon />}
                >
                    {t('aiWizard.duration.exercises')}
                </Button>
            </Stack>

            <Typography gutterBottom sx={{ textAlign: 'center' }}>
                {state.durationMode === 'minutes'
                    ? t('aiWizard.duration.targetTime', { value: state.durationValue })
                    : t('aiWizard.duration.targetCount', { value: state.durationValue })}
            </Typography>

            <Slider
                value={state.durationValue}
                onChange={(_, val) => updateState('durationValue', val as number)}
                min={state.durationMode === 'minutes' ? 15 : 2}
                max={state.durationMode === 'minutes' ? 120 : 12}
                step={state.durationMode === 'minutes' ? 5 : 1}
                marks
                valueLabelDisplay="auto"
                sx={{ mt: 2 }}
            />
        </Box>
    );

    const renderLevelStep = () => (
        <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            {[
                { value: 'beginner', label: t('aiWizard.level.beginner'), desc: t('aiWizard.level.beginnerDesc') },
                { value: 'experienced', label: t('aiWizard.level.experienced'), desc: t('aiWizard.level.experiencedDesc') }
            ].map((level) => (
                <Grid size={{ xs: 12, sm: 6 }} key={level.value}>
                    <Card
                        elevation={state.level === level.value ? 4 : 1}
                        sx={{ border: state.level === level.value ? '2px solid primary.main' : 'none' }}
                    >
                        <CardActionArea
                            onClick={() => { updateState('level', level.value); handleNext(); }}
                            sx={{ p: 3, textAlign: 'center', height: '100%' }}
                        >
                            <Typography variant="h6">{level.label}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{level.desc}</Typography>
                        </CardActionArea>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    const renderExercisesStep = () => (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>{t('aiWizard.exercises.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                {t('aiWizard.exercises.description')}
            </Typography>

            <FormControlLabel
                control={
                    <Switch
                        checked={state.useExisting}
                        onChange={(e) => updateState('useExisting', e.target.checked)}
                        size="medium"
                    />
                }
                label={state.useExisting ? t('aiWizard.exercises.yes') : t('aiWizard.exercises.no')}
            />

            {state.useExisting && (
                <Typography variant="caption" sx={{ display: 'block', mt: 2 }}>
                    {t('aiWizard.exercises.count', { count: exercises.length })}
                </Typography>
            )}
        </Box>
    );

    const renderSummaryStep = () => (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>{t('aiWizard.steps.summary')}</Typography>
            <Card variant="outlined">
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}><Typography color="text.secondary">{t('aiWizard.summary.location')}</Typography></Grid>
                        <Grid size={{ xs: 6 }}><Typography fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                            {state.location === 'home' ? t('aiWizard.location.home') : t('aiWizard.location.gym')}
                        </Typography></Grid>

                        <Grid size={{ xs: 6 }}><Typography color="text.secondary">{t('aiWizard.summary.muscles')}</Typography></Grid>
                        <Grid size={{ xs: 6 }}><Typography fontWeight="bold">{state.muscles.length ? state.muscles.map(getMuscleLabel).join(', ') : t('aiWizard.summary.fullBody')}</Typography></Grid>

                        <Grid size={{ xs: 6 }}><Typography color="text.secondary">{t('aiWizard.summary.type')}</Typography></Grid>
                        <Grid size={{ xs: 6 }}><Typography fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                            {state.type ? t(`aiWizard.type.${state.type}`) : '-'}
                        </Typography></Grid>

                        <Grid size={{ xs: 6 }}><Typography color="text.secondary">{t('aiWizard.summary.target')}</Typography></Grid>
                        <Grid size={{ xs: 6 }}><Typography fontWeight="bold">{state.durationValue} {state.durationMode === 'minutes' ? t('aiWizard.duration.minutes') : t('aiWizard.duration.exercises')}</Typography></Grid>

                        <Grid size={{ xs: 6 }}><Typography color="text.secondary">{t('aiWizard.summary.level')}</Typography></Grid>
                        <Grid size={{ xs: 6 }}><Typography fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                            {state.level ? t(`aiWizard.level.${state.level}`) : '-'}
                        </Typography></Grid>

                        <Grid size={{ xs: 6 }}><Typography color="text.secondary">{t('aiWizard.summary.source')}</Typography></Grid>
                        <Grid size={{ xs: 6 }}><Typography fontWeight="bold">{state.useExisting ? t('aiWizard.summary.libraryOnly') : t('aiWizard.summary.any')}</Typography></Grid>
                    </Grid>
                </CardContent>
            </Card>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                {t('aiWizard.summary.ready')}
            </Typography>
        </Box>
    );

    const getStepContent = (step: number) => {
        switch (step) {
            case 0: return renderLocationStep();
            case 1: return renderMusclesStep();
            case 2: return renderTypeStep();
            case 3: return renderDurationStep();
            case 4: return renderLevelStep();
            case 5: return renderExercisesStep();
            case 6: return renderSummaryStep();
            default: return 'Unknown step';
        }
    };

    return (
        <Box sx={{ width: '100%', p: 1 }}>
            {isMobile ? (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                        {t('aiWizard.step_counter', { current: activeStep + 1, total: steps.length })}
                    </Typography>
                    <Typography variant="h6" align="center" sx={{ fontWeight: 'bold' }}>
                        {t(steps[activeStep])}
                    </Typography>
                    <MobileStepper
                        variant="dots"
                        steps={steps.length}
                        position="static"
                        activeStep={activeStep}
                        sx={{ maxWidth: 400, flexGrow: 1, background: 'transparent', mt: 1 }}
                        nextButton={null}
                        backButton={null}
                    />
                </Box>
            ) : (
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{t(label)}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}

            <Box sx={{ minHeight: 400, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1 }}>
                    {getStepContent(activeStep)}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mt: 4, borderTop: '1px solid #eee' }}>
                    <Button
                        color="inherit"
                        disabled={activeStep === 0 || isLoading}
                        onClick={handleBack}
                        sx={{ mr: 1 }}
                    >
                        {t('aiWizard.button.back')}
                    </Button>
                    <Box sx={{ flex: '1 1 auto' }} />
                    {activeStep === steps.length - 1 ? (
                        <Button onClick={handleNext} variant="contained" disabled={isLoading}>
                            {isLoading ? t('aiWizard.button.generating') : t('aiWizard.button.generate')}
                        </Button>
                    ) : (
                        // Hide next button for Location (0), Type (2), and Level (4) steps as they auto-advance
                        ![0, 2, 4].includes(activeStep) && (
                            <Button onClick={handleNext} variant="contained">
                                {t('aiWizard.button.next')}
                            </Button>
                        )
                    )}
                </Box>
            </Box>
        </Box>
    );
};
